import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Calendar, Clock, Check, X, RefreshCw, ChevronLeft, ChevronRight, Video, User, MapPin, MessageSquare, Save, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AgentToursPage() {
  const { user, token } = useAuth();
  const { language } = useLanguage();
  const [bookings, setBookings] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [weekOffset, setWeekOffset] = useState(0);
  const [calendarData, setCalendarData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [respondTo, setRespondTo] = useState(null);
  const [responseAction, setResponseAction] = useState('');
  const [responseComment, setResponseComment] = useState('');
  const [newDate, setNewDate] = useState('');
  const [newTime, setNewTime] = useState('');
  const [savingAvail, setSavingAvail] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };
  const tt = (en, es) => language === 'es' ? es : en;
  const dayLabels = language === 'es'
    ? ['Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sabado', 'Domingo']
    : ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  const fetchData = async () => {
    try {
      const [bookRes, availRes] = await Promise.all([
        axios.get(`${API}/tours/my`, { headers }),
        axios.get(`${API}/tours/availability/${user.id}`),
      ]);
      setBookings(bookRes.data.bookings || []);
      setAvailability(availRes.data.slots || []);
    } catch { toast.error(tt('Failed to load', 'Error al cargar')); }
    setLoading(false);
  };

  useEffect(() => { if (token && user) fetchData(); }, [token, user]);

  useEffect(() => {
    if (!token || !user) return;
    const d = new Date();
    d.setDate(d.getDate() - d.getDay() + 1 + weekOffset * 7);
    const ws = d.toISOString().split('T')[0];
    axios.get(`${API}/tours/calendar/${user.id}?week_start=${ws}`, { headers })
      .then(r => setCalendarData(r.data))
      .catch(() => {});
  }, [token, user, weekOffset]);

  const saveAvailability = async () => {
    setSavingAvail(true);
    try {
      await axios.put(`${API}/tours/availability`, { slots: availability }, { headers });
      toast.success(tt('Availability saved!', 'Disponibilidad guardada!'));
    } catch { toast.error(tt('Failed to save', 'Error al guardar')); }
    setSavingAvail(false);
  };

  const toggleDay = (dow) => {
    const exists = availability.find(s => s.day_of_week === dow);
    if (exists) {
      setAvailability(availability.filter(s => s.day_of_week !== dow));
    } else {
      setAvailability([...availability, { day_of_week: dow, start_time: '09:00', end_time: '17:00' }]);
    }
  };

  const updateDayTime = (dow, field, value) => {
    setAvailability(availability.map(s => s.day_of_week === dow ? { ...s, [field]: value } : s));
  };

  const respondBooking = async () => {
    if (!respondTo) return;
    try {
      await axios.put(`${API}/tours/${respondTo.id}/respond`, {
        action: responseAction,
        new_date: newDate,
        new_time: newTime,
        comment: responseComment,
      }, { headers });
      toast.success(tt('Response sent!', 'Respuesta enviada!'));
      setRespondTo(null);
      setResponseAction('');
      setResponseComment('');
      setNewDate('');
      setNewTime('');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || tt('Failed', 'Error')); }
  };

  const pending = bookings.filter(b => b.status === 'pending' || b.status === 'rescheduled');
  const upcoming = bookings.filter(b => b.status === 'accepted' && b.date >= new Date().toISOString().split('T')[0]);
  const past = bookings.filter(b => b.status === 'accepted' && b.date < new Date().toISOString().split('T')[0]);

  const statusColors = {
    pending: 'bg-amber-100 text-amber-700',
    accepted: 'bg-green-100 text-green-700',
    rescheduled: 'bg-blue-100 text-blue-700',
    cancelled: 'bg-red-100 text-red-700',
  };
  const statusLabels = language === 'es'
    ? { pending: 'Pendiente', accepted: 'Aceptado', rescheduled: 'Reprogramado', cancelled: 'Cancelado' }
    : { pending: 'Pending', accepted: 'Accepted', rescheduled: 'Rescheduled', cancelled: 'Cancelled' };

  if (loading) return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-4">
      <Skeleton className="h-8 w-48" /><Skeleton className="h-48 rounded-xl" />
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="agent-tours-page">
      <div className="flex items-center gap-3 mb-6">
        <Video className="w-7 h-7 text-primary" />
        <div>
          <h1 className="font-heading text-xl md:text-2xl font-semibold">{tt('Tour Management', 'Gestion de Tours')}</h1>
          <p className="text-xs text-slate-500">{tt('Manage your virtual tour schedule and bookings', 'Gestiona tu agenda de tours virtuales y reservas')}</p>
        </div>
      </div>

      {/* Pending banner */}
      {pending.length > 0 && (
        <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4 mb-5 flex items-center gap-3" data-testid="pending-tours-banner">
          <Clock className="w-5 h-5 text-amber-600" />
          <p className="text-sm font-medium text-amber-800">
            {pending.length} {tt('pending tour request(s) need your response', 'solicitud(es) de tour pendientes necesitan tu respuesta')}
          </p>
        </div>
      )}

      <Tabs defaultValue="requests" className="space-y-4">
        <TabsList className="bg-slate-100 p-1 rounded-xl">
          <TabsTrigger value="requests" className="rounded-lg gap-1.5" data-testid="tours-tab-requests">
            <MessageSquare className="w-4 h-4" />{tt('Requests', 'Solicitudes')}
            {pending.length > 0 && <Badge className="ml-1 bg-amber-500 text-white text-xs h-5 px-1.5">{pending.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="calendar" className="rounded-lg gap-1.5" data-testid="tours-tab-calendar">
            <Calendar className="w-4 h-4" />{tt('Calendar', 'Calendario')}
          </TabsTrigger>
          <TabsTrigger value="availability" className="rounded-lg gap-1.5" data-testid="tours-tab-availability">
            <Clock className="w-4 h-4" />{tt('Availability', 'Disponibilidad')}
          </TabsTrigger>
        </TabsList>

        {/* ===== REQUESTS TAB ===== */}
        <TabsContent value="requests">
          <div className="space-y-3">
            {[...pending, ...upcoming, ...past].length > 0 ? [...pending, ...upcoming, ...past].map(b => (
              <Card key={b.id} className={b.status === 'pending' ? 'border-amber-200' : ''} data-testid={`booking-${b.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Video className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold text-sm">{b.property_title}</p>
                        <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><User className="w-3 h-3" />{b.buyer_name} ({b.buyer_email})</p>
                        <div className="flex items-center gap-3 mt-1.5">
                          <span className="text-xs font-medium text-slate-700 flex items-center gap-1"><Calendar className="w-3 h-3 text-primary" />{b.date}</span>
                          <span className="text-xs font-medium text-slate-700 flex items-center gap-1"><Clock className="w-3 h-3 text-primary" />{b.time} (30 min)</span>
                        </div>
                        {b.notes && <p className="text-xs text-slate-400 mt-1 italic">"{b.notes}"</p>}
                        {b.comments?.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {b.comments.map((c, i) => (
                              <p key={i} className="text-[10px] text-slate-400"><strong>{c.by}</strong> ({c.action}): {c.text} - {new Date(c.created_at).toLocaleDateString()}</p>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <Badge className={statusColors[b.status]}>{statusLabels[b.status]}</Badge>
                    </div>
                  </div>

                  {/* Action buttons for pending */}
                  {(b.status === 'pending' || b.status === 'rescheduled') && (
                    <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t">
                      <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white gap-1" data-testid={`accept-${b.id}`}
                        onClick={() => { setRespondTo(b); setResponseAction('accepted'); setResponseComment(''); respondBooking(); }}>
                        <Check className="w-3.5 h-3.5" />{tt('Accept', 'Aceptar')}
                      </Button>
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button size="sm" variant="outline" className="gap-1" data-testid={`reschedule-${b.id}`}
                            onClick={() => { setRespondTo(b); setResponseAction('reschedule'); }}>
                            <RefreshCw className="w-3.5 h-3.5" />{tt('Reschedule', 'Reprogramar')}
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-sm" aria-describedby="reschedule-desc">
                          <DialogHeader><DialogTitle>{tt('Reschedule Tour', 'Reprogramar Tour')}</DialogTitle></DialogHeader>
                          <p id="reschedule-desc" className="text-sm text-slate-500">{tt('Propose a new date and time.', 'Proponga una nueva fecha y hora.')}</p>
                          <div className="space-y-3 mt-2">
                            <div><Label className="text-xs">{tt('New Date', 'Nueva Fecha')}</Label><Input type="date" value={newDate} onChange={e => setNewDate(e.target.value)} className="mt-1" data-testid="new-date" /></div>
                            <div><Label className="text-xs">{tt('New Time', 'Nueva Hora')}</Label><Input type="time" value={newTime} onChange={e => setNewTime(e.target.value)} className="mt-1" data-testid="new-time" /></div>
                            <div><Label className="text-xs">{tt('Comment', 'Comentario')}</Label><Textarea value={responseComment} onChange={e => setResponseComment(e.target.value)} rows={2} className="mt-1" data-testid="reschedule-comment" /></div>
                            <Button onClick={respondBooking} disabled={!newDate || !newTime}
                              className="w-full bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C]" data-testid="confirm-reschedule">
                              {tt('Send New Date', 'Enviar Nueva Fecha')}
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                      <Button size="sm" variant="outline" className="gap-1 text-red-600 border-red-200" data-testid={`cancel-${b.id}`}
                        onClick={() => { setRespondTo(b); setResponseAction('cancelled'); setResponseComment(''); respondBooking(); }}>
                        <X className="w-3.5 h-3.5" />{tt('Cancel', 'Cancelar')}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )) : (
              <div className="text-center py-12 bg-white rounded-xl border border-border">
                <Video className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">{tt('No tour bookings yet', 'Sin reservas de tours aun')}</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* ===== CALENDAR TAB ===== */}
        <TabsContent value="calendar">
          <Card><CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <Button variant="outline" size="sm" onClick={() => setWeekOffset(weekOffset - 1)} className="gap-1" data-testid="cal-prev">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <p className="text-sm font-medium">
                {calendarData?.week_start} — {calendarData?.week_end}
              </p>
              <Button variant="outline" size="sm" onClick={() => setWeekOffset(weekOffset + 1)} className="gap-1" data-testid="cal-next">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            {calendarData?.bookings?.length > 0 ? (
              <div className="space-y-2">
                {calendarData.bookings.map(b => (
                  <div key={b.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg" data-testid={`cal-booking-${b.id}`}>
                    <div className="w-1.5 h-10 rounded-full flex-shrink-0" style={{ backgroundColor: b.status === 'accepted' ? '#22c55e' : b.status === 'pending' ? '#f59e0b' : '#3b82f6' }} />
                    <div className="flex-1">
                      <p className="font-medium text-sm">{b.property_title}</p>
                      <p className="text-xs text-slate-500">{b.buyer_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs font-medium">{b.date}</p>
                      <p className="text-xs text-primary font-semibold">{b.time}</p>
                    </div>
                    <Badge className={statusColors[b.status] + ' text-[10px]'}>{statusLabels[b.status]}</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400 text-center py-8">{tt('No tours scheduled this week', 'Sin tours programados esta semana')}</p>
            )}
          </CardContent></Card>
        </TabsContent>

        {/* ===== AVAILABILITY TAB ===== */}
        <TabsContent value="availability">
          <Card><CardContent className="p-5">
            <p className="font-semibold text-sm mb-1">{tt('Weekly Availability', 'Disponibilidad Semanal')}</p>
            <p className="text-xs text-slate-400 mb-4">{tt('Set the days and hours you are available for virtual tours (30-min slots).', 'Configura los dias y horas que estas disponible para tours virtuales (slots de 30 min).')}</p>
            <div className="space-y-2">
              {dayLabels.map((day, idx) => {
                const slot = availability.find(s => s.day_of_week === idx);
                const isActive = !!slot;
                return (
                  <div key={idx} className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${isActive ? 'bg-primary/5 border border-primary/20' : 'bg-slate-50 border border-transparent'}`}
                    data-testid={`avail-day-${idx}`}>
                    <button onClick={() => toggleDay(idx)}
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors flex-shrink-0 ${isActive ? 'bg-primary border-primary' : 'border-slate-300'}`}
                      data-testid={`toggle-day-${idx}`}>
                      {isActive && <Check className="w-3 h-3 text-white" />}
                    </button>
                    <span className={`text-sm font-medium w-24 ${isActive ? 'text-slate-800' : 'text-slate-400'}`}>{day}</span>
                    {isActive && (
                      <div className="flex items-center gap-2">
                        <Input type="time" value={slot.start_time} onChange={e => updateDayTime(idx, 'start_time', e.target.value)}
                          className="w-28 h-8 text-xs" data-testid={`start-${idx}`} />
                        <span className="text-xs text-slate-400">—</span>
                        <Input type="time" value={slot.end_time} onChange={e => updateDayTime(idx, 'end_time', e.target.value)}
                          className="w-28 h-8 text-xs" data-testid={`end-${idx}`} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <Button onClick={saveAvailability} disabled={savingAvail}
              className="mt-4 bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] gap-1" data-testid="save-availability-btn">
              {savingAvail ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              {tt('Save Availability', 'Guardar Disponibilidad')}
            </Button>
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
